import { useTranslation } from '@rhino-project/core/i18n';
import { useCallback, useMemo } from 'react';

import { BaseAuthedPage } from '../BaseAuthedPage';
import { hasOrganizationsModule } from '@rhino-project/core/utils';
import { getSettingsPath } from '@rhino-project/core/utils';
import {
  Tabs,
  TabList,
  TabListContainer,
  Tab,
  TabIndicator
} from '@heroui/react';
import { Outlet, useLocation } from '@tanstack/react-router';
import { useBaseOwnerPath } from '../../hooks';

export const OrganizationSettingsPage = () => {
  const { t } = useTranslation();
  const { build } = useBaseOwnerPath();
  const { pathname } = useLocation();

  const showSubscriptions = useMemo(() => hasOrganizationsModule(), []);

  const settingsBuild = useCallback(
    (tabId: string) => build(`${getSettingsPath()}${tabId ? `/${tabId}` : ''}`),
    [build]
  );

  // A Tab's `id` is the React Aria collection key and MUST stay stable across
  // renders. The base owner resolves asynchronously on reload, so build() (and
  // any path derived from it) changes between renders -- keying a Tab on the
  // built path trips React Aria's "Cannot change the id of an item". Key on the
  // stable segment instead; href still uses the built path for navigation.
  const tabs = useMemo(
    () => [
      {
        id: 'profile',
        label: t('rhino.frontend.messages.profile'),
        segment: ''
      },
      {
        id: 'access',
        label: t('rhino.frontend.messages.access'),
        segment: 'access'
      },
      ...(showSubscriptions
        ? [
            {
              id: 'subscription',
              label: t('rhino.frontend.messages.subscription'),
              segment: 'subscription'
            }
          ]
        : [])
    ],
    [showSubscriptions, t]
  );

  const selectedKey = useMemo(() => {
    const match = tabs.find(
      (tab) => tab.segment && pathname.endsWith(`/${tab.segment}`)
    );
    return match ? match.id : 'profile';
  }, [pathname, tabs]);

  return (
    <BaseAuthedPage>
      <Tabs selectedKey={selectedKey}>
        <TabListContainer>
          <TabList
            aria-label={t('rhino.frontend.messages.organization_settings')}
          >
            {tabs.map((tab) => (
              <Tab key={tab.id} id={tab.id} href={settingsBuild(tab.segment)}>
                {tab.label}
                <TabIndicator />
              </Tab>
            ))}
          </TabList>
        </TabListContainer>
      </Tabs>
      <div className="mt-4">
        <Outlet />
      </div>
    </BaseAuthedPage>
  );
};
